import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    Table,
    Tag,
    Button,
    Space,
    message,
    Typography,
    Input,
    Select,
    Modal,
    Form,
    Dropdown,
    Alert,
    InputNumber,
    Checkbox,
    DatePicker,
    Badge,
    Tooltip,
    Statistic,
    Row,
    Col,
    Divider,
    notification,
} from 'antd';
import {
    ReloadOutlined,
    EyeOutlined,
    SearchOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    StopOutlined,
    LockOutlined,
    UnlockOutlined,
    SwapOutlined,
    MoreOutlined,
    KeyOutlined,
    CalendarOutlined,
    DownloadOutlined,
    SyncOutlined,
    DollarOutlined,
    WarningOutlined,
    CrownOutlined,
    CloudSyncOutlined,
    ImportOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import AdminTransferAction from '../../components/AdminTransferAction';
import { registrarService } from '../../services/registrarService';
import moment from 'moment';
import debounce from 'lodash/debounce';

import { adminPath } from '../../../shared/config/paths';
import DomainStatusTag, { DOMAIN_STATUS_OPTIONS } from '../../../shared/components/DomainStatusTag';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

/**
 * Admin: 全域名管理
 *
 * - 服务端分页 + 搜索/筛选 + 统计（/api/admin/domain?page=）
 * - 多筛选：domain / status / user_id / registrar_id / 到期范围 / 自动续费
 * - 批量选择 + 批量同步 / 批量续费 / 批量暂停
 * - CSV 导出当前筛选
 * - 操作前 admin 二次确认 modal（force-renew / suspend / change-owner）
 * - 顶部 "Acting as admin" 横幅，提醒管理员当前是在动其他用户的数据
 */
const DomainManagement = () => {
    const navigate = useNavigate();
    const [domains, setDomains] = useState([]);
    const [registrars, setRegistrars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [total, setTotal] = useState(0);
    const [serverStats, setServerStats] = useState(null);
    const pageRef = useRef({ current: 1, pageSize: 20 });
    const [pageState, setPageState] = useState(pageRef.current);
    const [searchParams] = useSearchParams();
    const [filters, setFilters] = useState(() => (searchParams.get('search') ? { domain: searchParams.get('search').trim() } : {}));
    const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [actionModal, setActionModal] = useState({ visible: false, type: '', domain: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [actionForm] = Form.useForm();

    // 批量操作
    const [bulkModal, setBulkModal] = useState({ visible: false, type: '' });
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkConfirmText, setBulkConfirmText] = useState('');

    // Force-renew (true registrar renewal, debits the owner)
    const [renewModal, setRenewModal] = useState({ visible: false, domain: null });
    const [renewForm] = Form.useForm();

    // 从注册商同步既有域名
    const [syncModal, setSyncModal] = useState({ visible: false });
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [syncForm] = Form.useForm();

    // 宽限/赎回期域名
    const [graceModal, setGraceModal] = useState({ visible: false });
    const [graceLoading, setGraceLoading] = useState(false);
    const [graceRows, setGraceRows] = useState([]);
    const [gracePeriod, setGracePeriod] = useState(undefined);
    const [restoringId, setRestoringId] = useState(null);

    // 批量导入既有域名记录
    const [importModal, setImportModal] = useState({ visible: false });
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importForm] = Form.useForm();

    // 单域名同步日志
    const [syncLogModal, setSyncLogModal] = useState({ visible: false, domain: null });
    const [syncLogLoading, setSyncLogLoading] = useState(false);
    const [syncLogRows, setSyncLogRows] = useState([]);

    useEffect(() => {
        loadDomains(filters);
        loadRegistrars();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 将 UI 过滤器映射为后端可识别的查询参数（registrar_ids / auto_renew / expires_before|after）
    const buildApiParams = (f = {}) => {
        const p = {};
        // 后端 getIndex 只认 `domain` 参数（LIKE 匹配 domain 列）
        if (f.domain) p.domain = f.domain;
        if (f.status) p.status = f.status;
        if (f.user_id) p.user_id = f.user_id;
        if (f.registrar_ids && f.registrar_ids.length > 0) p.registrar_ids = f.registrar_ids;
        if (f.auto_renew !== undefined && f.auto_renew !== null && f.auto_renew !== '') {
            p.auto_renew = f.auto_renew;
        }
        if (f.expiry_range && f.expiry_range.length === 2) {
            p.expires_after = moment(f.expiry_range[0]).format('YYYY-MM-DD');
            p.expires_before = moment(f.expiry_range[1]).format('YYYY-MM-DD 23:59:59');
        }
        return p;
    };

    const loadDomains = useCallback(async (params = {}, pg = pageRef.current) => {
        pageRef.current = pg;
        setPageState(pg);
        setLoading(true);
        try {
            const response = await registrarService.getUserDomains({ ...buildApiParams(params), page: pg.current, page_size: pg.pageSize });
            if (response.success) {
                setLoadError('');
                const list = Array.isArray(response.data) ? response.data : (response.data?.list || []);
                setDomains(list);
                setTotal(Array.isArray(response.data) ? list.length : (response.data?.total || 0));
                setServerStats(response.data?.stats || null);
            } else {
                setLoadError(response.error || response.message || '加载失败');
            }
        } catch (error) {
            setLoadError('加载失败：' + (error.message || error.error || ''));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadRegistrars = async () => {
        try {
            const resp = await registrarService.getRegistrars();
            if (resp.success) setRegistrars(resp.data || []);
        } catch (e) {
            // non-fatal — filters will just show empty
        }
    };

    // 防抖 — 仅对文本搜索字段使用
    const debouncedReload = useMemo(
        () => debounce((next) => loadDomains(next), 350),
        [loadDomains]
    );
    useEffect(() => () => debouncedReload.cancel(), [debouncedReload]);

    const setFilter = (key, value, opts = {}) => {
        const next = { ...filters, [key]: value };
        // treat empty string / undefined / null / empty array as "unset"
        if (value === undefined || value === null || value === ''
            || (Array.isArray(value) && value.length === 0)) {
            delete next[key];
        }
        setFilters(next);
        pageRef.current = { ...pageRef.current, current: 1 };
        if (opts.debounce) {
            debouncedReload(next);
        } else {
            loadDomains(next);
        }
    };

    // 服务端已按筛选分页，表格直接展示当前页
    const filteredDomains = domains;

    const handleViewDetail = (record) => {
        navigate(adminPath(`/domain-registration/detail/${record.id}`));
    };

    const handleAction = (type, domain) => {
        actionForm.resetFields();
        setActionModal({ visible: true, type, domain });
    };

    const executeAction = async () => {
        setActionLoading(true);
        try {
            const { type, domain } = actionModal;
            const values = await actionForm.validateFields().catch(() => ({}));

            // Destructive ops: require admin to type the domain into a confirmation field
            // (force the explicit "yes I know whose data I'm touching" step).
            const destructive = ['suspend', 'hold', 'change_owner'];
            if (destructive.includes(type)) {
                if ((values.confirm_domain || '').trim() !== domain.domain) {
                    setActionLoading(false);
                    message.error('请准确输入域名以确认操作');
                    return;
                }
            }

            let response;
            switch (type) {
                case 'hold':
                    response = await registrarService.holdDomain(domain.id, values.reason || '');
                    break;
                case 'unhold':
                    response = await registrarService.unholdDomain(domain.id);
                    break;
                case 'lock_transfer':
                    response = await registrarService.lockTransfer(domain.id);
                    break;
                case 'unlock_transfer':
                    response = await registrarService.unlockTransfer(domain.id);
                    break;
                case 'change_owner':
                    response = await registrarService.changeDomainOwner(domain.id, values.new_user_id);
                    break;
                case 'extend':
                    response = await registrarService.extendDomain(domain.id, values.days, values.free || false);
                    break;
                case 'suspend':
                    response = await registrarService.suspendDomain(domain.id, values.reason || '');
                    break;
                case 'resume':
                    response = await registrarService.resumeDomain(domain.id);
                    break;
                case 'sync_from_registrar':
                    response = await registrarService.triggerDomainSync(domain.id);
                    break;
                case 'get_auth_code':
                    response = await registrarService.getDomainAuthCode(domain.id);
                    if (response.success && response.data?.auth_code) {
                        Modal.info({
                            title: '域名授权码',
                            content: (
                                <div>
                                    <p>域名: {domain.domain}</p>
                                    <p style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 'bold' }}>
                                        授权码: {response.data.auth_code}
                                    </p>
                                </div>
                            ),
                        });
                        setActionModal({ visible: false, type: '', domain: null });
                        setActionLoading(false);
                        return;
                    }
                    break;
                default:
                    throw new Error('未知操作');
            }

            if (response.success || response.code === 0) {
                message.success('操作成功');
                setActionModal({ visible: false, type: '', domain: null });
                loadDomains(filters);
            } else {
                message.error(response.error || response.message || '操作失败');
            }
        } catch (error) {
            message.error(error.message || error.error || '操作失败');
        } finally {
            setActionLoading(false);
        }
    };

    // -------- Force renew (charge owner, sync to registrar) --------
    const openRenewModal = (record) => {
        renewForm.resetFields();
        renewForm.setFieldsValue({ years: 1 });
        setRenewModal({ visible: true, domain: record });
    };

    const executeRenew = async () => {
        try {
            const values = await renewForm.validateFields();
            if ((values.confirm_domain || '').trim() !== renewModal.domain.domain) {
                message.error('请准确输入域名以确认续费');
                return;
            }
            setActionLoading(true);
            const resp = await registrarService.renewDomain(renewModal.domain.id, values.years);
            if (resp.success || resp.code === 0) {
                message.success('续费成功');
                setRenewModal({ visible: false, domain: null });
                loadDomains(filters);
            } else {
                message.error(resp.error || resp.message || '续费失败');
            }
        } catch (err) {
            if (!err.errorFields) message.error(err.message || '续费失败');
        } finally {
            setActionLoading(false);
        }
    };

    // -------- 从注册商同步既有域名 --------
    const openSyncModal = () => {
        syncForm.resetFields();
        setSyncResult(null);
        setSyncModal({ visible: true });
    };

    const executeSync = async () => {
        try {
            const values = await syncForm.validateFields();
            setSyncLoading(true);
            const resp = await registrarService.syncDomainsFromRegistrar(values.registrar_id);
            if (resp.code === 0 || resp.success) {
                setSyncResult(resp.data || null);
                message.success(`同步完成，更新 ${resp.data?.synced ?? 0} 个域名`);
                loadDomains(filters);
            } else {
                message.error(resp.message || resp.error || '同步失败');
            }
        } catch (err) {
            if (!err.errorFields) message.error(err.message || err.error || '同步失败');
        } finally {
            setSyncLoading(false);
        }
    };

    // -------- 宽限/赎回期域名 --------
    const openGraceModal = () => {
        setGraceModal({ visible: true });
        loadGraceDomains(gracePeriod);
    };

    const loadGraceDomains = async (period) => {
        setGraceLoading(true);
        try {
            const params = { limit: 200 };
            if (period) params.period = period;
            const resp = await registrarService.getGraceRedemptionDomains(params);
            if (resp.code === 0 || resp.success) {
                setGraceRows(resp.data?.list || []);
            } else {
                message.error(resp.message || resp.error || '加载失败');
            }
        } catch (err) {
            message.error(err.message || err.error || '加载失败');
        } finally {
            setGraceLoading(false);
        }
    };

    const handleRestore = async (record) => {
        setRestoringId(record.id);
        try {
            const resp = await registrarService.restoreDomain(record.id);
            if (resp.code === 0 || resp.success) {
                message.success('域名恢复成功');
                loadGraceDomains(gracePeriod);
                loadDomains(filters);
            } else {
                message.error(resp.message || resp.error || '恢复失败');
            }
        } catch (err) {
            message.error(err.message || err.error || '恢复失败');
        } finally {
            setRestoringId(null);
        }
    };

    // -------- 批量导入既有域名记录 --------
    const openImportModal = () => {
        importForm.resetFields();
        setImportResult(null);
        setImportModal({ visible: true });
    };

    // 解析文本：每行 "domain,user_id[,registrar_order_id]"
    const parseImportRows = (text) => {
        const rows = [];
        (text || '').split('\n').forEach((line) => {
            const trimmed = line.trim();
            if (!trimmed) return;
            const parts = trimmed.split(/[,\t]/).map((p) => p.trim());
            const domain = (parts[0] || '').toLowerCase();
            const userId = parts[1] ? Number(parts[1]) : null;
            if (!domain || !userId) return;
            const item = { domain, user_id: userId };
            if (parts[2]) item.registrar_order_id = parts[2];
            rows.push(item);
        });
        return rows;
    };

    const executeImport = async () => {
        try {
            const values = await importForm.validateFields();
            const rows = parseImportRows(values.rows);
            if (rows.length === 0) {
                message.error('未解析到有效行（格式：domain,user_id[,registrar_order_id]，每行一个）');
                return;
            }
            setImportLoading(true);
            const resp = await registrarService.batchImportDomains(values.registrar_id, rows);
            if (resp.code === 0 || resp.success) {
                setImportResult(resp.data || { total: rows.length });
                message.success(`导入完成，成功 ${resp.data?.imported ?? 0} 个`);
                loadDomains(filters);
            } else {
                message.error(resp.message || resp.error || '导入失败');
            }
        } catch (err) {
            if (!err.errorFields) message.error(err.message || err.error || '导入失败');
        } finally {
            setImportLoading(false);
        }
    };

    // -------- 单域名同步日志 --------
    const openSyncLogModal = async (domain) => {
        setSyncLogModal({ visible: true, domain });
        setSyncLogRows([]);
        setSyncLogLoading(true);
        try {
            const resp = await registrarService.getDomainSyncLogs(domain.id, { limit: 100 });
            if (resp.code === 0 || resp.success) {
                setSyncLogRows(resp.data?.list || resp.data || []);
            } else {
                message.error(resp.message || resp.error || '加载同步日志失败');
            }
        } catch (err) {
            message.error(err.message || err.error || '加载同步日志失败');
        } finally {
            setSyncLogLoading(false);
        }
    };

    // -------- Bulk ops --------
    const openBulkModal = (type) => {
        if (selectedRowKeys.length === 0) {
            message.warning('请先选择域名');
            return;
        }
        setBulkResult(null);
        setBulkConfirmText('');
        setBulkModal({ visible: true, type });
    };

    // 需要二次确认（输入关键词）的高危批量操作
    const BULK_CONFIRM_KEYWORD = { suspend: 'SUSPEND', hold: 'HOLD' };

    const executeBulk = async () => {
        const { type } = bulkModal;
        const ids = [...selectedRowKeys];
        if (ids.length === 0) return;

        // 高危批量操作：要求管理员显式键入关键词确认
        const keyword = BULK_CONFIRM_KEYWORD[type];
        if (keyword && bulkConfirmText.trim().toUpperCase() !== keyword) {
            message.error(`请输入 ${keyword} 以确认对 ${ids.length} 个域名的批量操作`);
            return;
        }

        setBulkLoading(true);
        const results = [];
        try {
            // No bulk endpoint in backend (see ADMIN_DOMAIN_UX_FINDINGS.md gap #2-3).
            // Fan out per-domain calls. For sync we have triggerBatchSync, prefer it.
            if (type === 'sync') {
                const resp = await registrarService.triggerBatchSync({ domain_ids: ids });
                if (resp.code === 0 || resp.success) {
                    results.push({ ok: true, count: resp.data?.scheduled_count ?? ids.length });
                } else {
                    results.push({ ok: false, error: resp.message || resp.error });
                }
            } else {
                for (const id of ids) {
                    try {
                        let r;
                        if (type === 'suspend') r = await registrarService.suspendDomain(id, '批量暂停');
                        else if (type === 'resume') r = await registrarService.resumeDomain(id);
                        else if (type === 'hold') r = await registrarService.holdDomain(id, '批量Hold');
                        else throw new Error('未知批量操作');
                        results.push({ id, ok: !!(r.success || r.code === 0), msg: r.message || r.error });
                    } catch (e) {
                        results.push({ id, ok: false, msg: e.message || String(e) });
                    }
                }
            }
            const ok = results.filter((r) => r.ok).length;
            const fail = results.length - ok;
            setBulkResult({ ok, fail, results });
            if (fail === 0) {
                notification.success({
                    message: '批量操作完成',
                    description: `成功 ${ok === 1 && results[0].count ? results[0].count : ok} 个域名`,
                });
            } else {
                notification.warning({
                    message: '部分失败',
                    description: `成功 ${ok}，失败 ${fail}`,
                });
            }
            setSelectedRowKeys([]);
            loadDomains(filters);
        } finally {
            setBulkLoading(false);
        }
    };

    // -------- CSV export of currently-filtered set --------
    const exportCsv = async () => {
        let rows = [];
        try {
            const resp = await registrarService.getUserDomains({ ...buildApiParams(filters), page: 1, page_size: 5000, export: 1 });
            rows = resp?.data?.list || [];
        } catch (e) {
            message.error('导出失败');
            return;
        }
        if (rows.length === 0) {
            message.warning('当前筛选无可导出数据');
            return;
        }
        const headers = [
            'id', 'domain', 'tld', 'user_id', 'username', 'registrar', 'status',
            'registered_at', 'expires_at', 'days_to_expiry', 'auto_renew',
            'whois_privacy', 'domain_lock'
        ];
        const lines = [headers.join(',')];
        rows.forEach((d) => {
            const days = d.expires_at ? moment(d.expires_at).diff(moment(), 'days') : '';
            const cols = [
                d.id,
                d.domain_unicode || d.domain,
                d.tld_unicode || d.tld || '',
                d.user_id,
                d.username || d.user?.username || '',
                d.registrar_name || d.registrar?.name || '',
                d.status || '',
                d.registered_at || '',
                d.expires_at || '',
                days,
                d.auto_renew ? '是' : '否',
                d.whois_privacy ? '是' : '否',
                d.domain_lock ? '是' : '否',
            ];
            lines.push(cols.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `admin-domains-${moment().format('YYYYMMDD-HHmmss')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        message.success(`已导出 ${rows.length} 条域名`);
    };

    // -------- Action menu items per row --------
    const getActionItems = (record) => {
        const items = [];
        const status = record.status;

        items.push({ key: 'force_renew', label: '强制续费 (扣费)', icon: <DollarOutlined /> });
        items.push({ key: 'sync_from_registrar', label: '从注册商同步', icon: <SyncOutlined /> });

        if (!record.is_held) {
            items.push({ key: 'hold', label: 'Hold域名', icon: <StopOutlined />, danger: true });
        } else {
            items.push({ key: 'unhold', label: '解除Hold', icon: <PlayCircleOutlined /> });
        }

        if (record.is_locked || record.domain_lock) {
            items.push({ key: 'unlock_transfer', label: '解锁转移', icon: <UnlockOutlined /> });
        } else {
            items.push({ key: 'lock_transfer', label: '锁定转移', icon: <LockOutlined /> });
        }

        if (status === 'active') {
            items.push({ key: 'suspend', label: '暂停服务', icon: <PauseCircleOutlined />, danger: true });
        }
        if (status === 'suspended') {
            items.push({ key: 'resume', label: '恢复服务', icon: <PlayCircleOutlined /> });
        }

        items.push({ key: 'get_auth_code', label: '获取授权码', icon: <KeyOutlined /> });
        items.push({ key: 'sync_logs', label: '同步日志', icon: <HistoryOutlined /> });
        items.push({ key: 'extend', label: '延长到期时间', icon: <CalendarOutlined /> });

        return items;
    };

    const handleMenuClick = (key, record) => {
        if (key === 'force_renew') {
            openRenewModal(record);
        } else if (key === 'sync_logs') {
            openSyncLogModal(record);
        } else {
            handleAction(key, record);
        }
    };

    const getActionModalTitle = () => {
        const titles = {
            hold: 'Hold域名（管理员操作）',
            unhold: '解除Hold',
            lock_transfer: '锁定转移',
            unlock_transfer: '解锁转移',
            change_owner: '转移所有权（管理员操作）',
            extend: '延长到期时间（管理员操作）',
            suspend: '暂停服务（管理员操作）',
            resume: '恢复服务',
            get_auth_code: '获取授权码',
            sync_from_registrar: '从注册商同步',
        };
        return titles[actionModal.type] || '操作';
    };

    const renderActionModalContent = () => {
        const { type, domain } = actionModal;
        if (!domain) return null;

        const adminWarning = (
            <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="管理员操作 — 你正在操作其他用户的数据"
                description={`域名 ${domain.domain} 属于用户 ${domain.username || domain.user?.username} (ID: ${domain.user_id})`}
            />
        );

        switch (type) {
            case 'hold':
                return (
                    <Form form={actionForm} layout="vertical">
                        {adminWarning}
                        <Alert
                            message="Hold域名将使其无法解析"
                            type="error"
                            showIcon
                            style={{ marginBottom: 12 }}
                        />
                        <Form.Item name="reason" label="Hold原因">
                            <Input.TextArea rows={3} placeholder="请输入Hold原因（可选）" />
                        </Form.Item>
                        <Form.Item
                            name="confirm_domain"
                            label={`输入 "${domain.domain}" 以确认`}
                            rules={[{ required: true, message: '请确认域名' }]}
                        >
                            <Input placeholder={domain.domain} />
                        </Form.Item>
                    </Form>
                );
            case 'suspend':
                return (
                    <Form form={actionForm} layout="vertical">
                        {adminWarning}
                        <Form.Item name="reason" label="暂停原因">
                            <Input.TextArea rows={3} placeholder="请输入暂停原因（可选）" />
                        </Form.Item>
                        <Form.Item
                            name="confirm_domain"
                            label={`输入 "${domain.domain}" 以确认`}
                            rules={[{ required: true, message: '请确认域名' }]}
                        >
                            <Input placeholder={domain.domain} />
                        </Form.Item>
                    </Form>
                );
            case 'change_owner':
                return (
                    <Form form={actionForm} layout="vertical">
                        {adminWarning}
                        <Alert
                            type="error"
                            showIcon
                            message="此操作会改变域名所属用户，将影响计费与配额"
                            style={{ marginBottom: 12 }}
                        />
                        <Form.Item name="new_user_id" label="新用户ID" rules={[{ required: true, message: '请输入用户ID' }]}>
                            <InputNumber min={1} placeholder="请输入新用户ID" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="confirm_domain"
                            label={`输入 "${domain.domain}" 以确认`}
                            rules={[{ required: true, message: '请确认域名' }]}
                        >
                            <Input placeholder={domain.domain} />
                        </Form.Item>
                    </Form>
                );
            case 'extend':
                return (
                    <Form form={actionForm} layout="vertical">
                        {adminWarning}
                        <p>当前到期时间: <Text strong>{domain.expires_at ? moment(domain.expires_at).format('YYYY-MM-DD') : '-'}</Text></p>
                        <Form.Item name="days" label="延长天数" rules={[{ required: true, message: '请输入天数' }]}>
                            <InputNumber min={1} max={3650} placeholder="请输入天数" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="free" valuePropName="checked" initialValue={false}>
                            <Checkbox>免费延期（管理员特权，不调注册商，仅改本地到期日）</Checkbox>
                        </Form.Item>
                    </Form>
                );
            case 'unhold':
            case 'lock_transfer':
            case 'unlock_transfer':
            case 'resume':
            case 'sync_from_registrar':
                return (
                    <>
                        {adminWarning}
                        <p>对域名 <Text strong>{domain.domain}</Text> 执行此操作？</p>
                    </>
                );
            case 'get_auth_code':
                return (
                    <>
                        {adminWarning}
                        <p>获取域名 <Text strong>{domain.domain}</Text> 的转移授权码（EPP code）？</p>
                    </>
                );
            default:
                return null;
        }
    };

    const getDaysUntilExpiry = (expiresAt) => {
        if (!expiresAt) return null;
        return moment(expiresAt).diff(moment(), 'days');
    };

    // 统计概览
    const stats = useMemo(() => serverStats || { total, active: 0, expiring30: 0, expired: 0 }, [serverStats, total]);

    const columns = [
        {
            title: '域名',
            dataIndex: 'domain',
            key: 'domain',
            fixed: 'left',
            width: 220,
            render: (domain, record) => (
                <Space direction="vertical" size={0}>
                    <a onClick={() => handleViewDetail(record)} style={{ fontWeight: 500 }} data-testid="domain-link">
                        {record.domain_unicode || domain}
                    </a>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        TLD: {record.tld_unicode || record.tld}
                    </Text>
                </Space>
            ),
        },
        {
            title: '用户',
            key: 'user',
            width: 160,
            ellipsis: true,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <a onClick={() => navigate(adminPath(`/users/${record.user_id}`))}>{record.username || record.user?.username || '-'}</a>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        ID: {record.user_id}
                    </Text>
                </Space>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => <DomainStatusTag status={status} />,
        },
        {
            title: '注册商',
            dataIndex: 'registrar_name',
            key: 'registrar_name',
            width: 130,
            render: (n, r) => n || r.registrar?.name || '-',
        },
        {
            title: '注册时间',
            dataIndex: 'registered_at',
            key: 'registered_at',
            width: 110,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '过期时间',
            dataIndex: 'expires_at',
            key: 'expires_at',
            width: 140,
            sorter: (a, b) => moment(a.expires_at || 0).diff(moment(b.expires_at || 0)),
            render: (date) => {
                if (!date) return '-';
                const days = getDaysUntilExpiry(date);
                const dateStr = moment(date).format('YYYY-MM-DD');
                let color = 'default';
                if (days < 0) color = 'error';
                else if (days < 30) color = 'warning';
                else if (days < 90) color = 'processing';
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{dateStr}</Text>
                        {days >= 0 ? (
                            <Tag color={color} style={{ fontSize: 11 }}>剩余 {days} 天</Tag>
                        ) : (
                            <Tag color={color} style={{ fontSize: 11 }}>已过期 {Math.abs(days)} 天</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: '自动续费',
            dataIndex: 'auto_renew',
            key: 'auto_renew',
            width: 90,
            render: (v) => v ? <Tag color="green">开启</Tag> : <Tag>关闭</Tag>,
        },
        {
            title: '隐私 / 锁定',
            key: 'flags',
            width: 110,
            render: (_, r) => (
                <Space size={4} wrap>
                    {r.whois_privacy ? <Tag color="green">隐私</Tag> : null}
                    {r.domain_lock ? <Tag color="orange" icon={<LockOutlined />}>锁定</Tag> : null}
                </Space>
            ),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <AdminTransferAction type="domain" id={record.id} name={record.domain} onDone={() => loadDomains(filters)}>
                        <Tooltip title="转移给其他账号"><Button type="link" size="small" icon={<SwapOutlined />} /></Tooltip>
                    </AdminTransferAction>
                    <Tooltip title="查看详情">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetail(record)}
                            data-testid="row-view-btn"
                        />
                    </Tooltip>
                    <Dropdown
                        menu={{
                            items: getActionItems(record),
                            onClick: ({ key }) => handleMenuClick(key, record),
                        }}
                        trigger={['click']}
                    >
                        <Button type="default" size="small" icon={<MoreOutlined />} data-testid="row-action-btn" />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                icon={<CrownOutlined />}
                banner
                message="管理员视图：全平台域名，操作直接影响用户数据与计费"
                data-testid="acting-as-admin-banner"
            />

            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col xs={12} sm={6}>
                    <Card size="small"><Statistic title="筛选结果" value={stats.total} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small"><Statistic title="活跃" value={stats.active} valueStyle={{ color: '#52c41a' }} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small"><Statistic title="30天内到期" value={stats.expiring30} valueStyle={{ color: '#faad14' }} /></Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small"><Statistic title="已过期" value={stats.expired} valueStyle={{ color: '#f5222d' }} /></Card>
                </Col>
            </Row>

            <Card
                title={<Title level={4} style={{ margin: 0 }}>域名管理</Title>}
                bordered={false}
                extra={
                    <Space wrap>
                        <Button
                            icon={<CloudSyncOutlined />}
                            onClick={openSyncModal}
                            data-testid="sync-from-registrar-btn"
                        >
                            从注册商同步
                        </Button>
                        <Button
                            icon={<ImportOutlined />}
                            onClick={openImportModal}
                            data-testid="batch-import-btn"
                        >
                            批量导入
                        </Button>
                        <Button
                            icon={<WarningOutlined />}
                            onClick={openGraceModal}
                            data-testid="grace-redemption-btn"
                        >
                            宽限/赎回期
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportCsv}
                            data-testid="export-csv-btn"
                        >
                            导出CSV
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => loadDomains(filters)}
                            loading={loading}
                            data-testid="refresh-btn"
                        >
                            刷新
                        </Button>
                    </Space>
                }
            >
                <Space wrap style={{ marginBottom: 12 }} size={[8, 8]}>
                    <Input
                        placeholder="域名 / 用户名 / 邮箱"
                        prefix={<SearchOutlined />}
                        style={{ width: 220 }}
                        allowClear
                        value={searchInput}
                        onChange={(e) => { setSearchInput(e.target.value); setFilter('domain', e.target.value.trim(), { debounce: true }); }}
                        data-testid="search-domain"
                    />
                    <Select
                        placeholder="状态"
                        style={{ width: 130 }}
                        allowClear
                        onChange={(v) => setFilter('status', v)}
                        options={DOMAIN_STATUS_OPTIONS.filter((o) => o.value !== 'pending_delete')}
                    />
                    <Select
                        mode="multiple"
                        placeholder="注册商（多选）"
                        style={{ minWidth: 200 }}
                        allowClear
                        onChange={(v) => setFilter('registrar_ids', v)}
                        options={registrars.map((r) => ({ value: r.id, label: r.name }))}
                    />
                    <Select
                        placeholder="自动续费"
                        style={{ width: 130 }}
                        allowClear
                        onChange={(v) => setFilter('auto_renew', v)}
                    >
                        <Select.Option value="true">开启</Select.Option>
                        <Select.Option value="false">关闭</Select.Option>
                    </Select>
                    <Input
                        placeholder="用户ID"
                        style={{ width: 120 }}
                        allowClear
                        onChange={(e) => setFilter('user_id', e.target.value, { debounce: true })}
                    />
                    <RangePicker
                        placeholder={['到期开始', '到期结束']}
                        onChange={(v) => setFilter('expiry_range', v)}
                    />
                </Space>

                {selectedRowKeys.length > 0 && (
                    <Alert
                        style={{ marginBottom: 12 }}
                        type="info"
                        showIcon
                        message={
                            <Space wrap>
                                <span>已选择 {selectedRowKeys.length} 个域名：</span>
                                <Button size="small" icon={<SyncOutlined />} onClick={() => openBulkModal('sync')} data-testid="bulk-sync-btn">
                                    批量同步
                                </Button>
                                <Button size="small" icon={<PauseCircleOutlined />} onClick={() => openBulkModal('suspend')} danger>
                                    批量暂停
                                </Button>
                                <Button size="small" icon={<StopOutlined />} onClick={() => openBulkModal('hold')} danger>
                                    批量Hold
                                </Button>
                                <Button size="small" icon={<PlayCircleOutlined />} onClick={() => openBulkModal('resume')}>
                                    批量恢复
                                </Button>
                                <Button size="small" onClick={() => setSelectedRowKeys([])}>清除选择</Button>
                            </Space>
                        }
                    />
                )}

                {loadError && (
                    <Alert type="error" showIcon style={{ marginBottom: 12 }} message={loadError}
                        action={<Button size="small" onClick={() => loadDomains(filters)}>重试</Button>} />
                )}
                <Table
                    columns={columns}
                    dataSource={filteredDomains}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1250 }}
                    sticky
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                        current: pageState.current,
                        pageSize: pageState.pageSize,
                        total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['20', '50', '100', '200'],
                        showTotal: (n) => `共 ${n} 条记录`,
                        onChange: (current, pageSize) => loadDomains(filters, { current: pageSize !== pageState.pageSize ? 1 : current, pageSize }),
                    }}
                />
            </Card>

            {/* 单域名操作模态框 */}
            <Modal
                title={getActionModalTitle()}
                open={actionModal.visible}
                onOk={executeAction}
                onCancel={() => setActionModal({ visible: false, type: '', domain: null })}
                confirmLoading={actionLoading}
                okText="确定执行"
                cancelText="取消"
                okButtonProps={{
                    danger: ['hold', 'suspend', 'change_owner'].includes(actionModal.type),
                    'data-testid': 'action-confirm-btn',
                }}
                destroyOnClose
            >
                {renderActionModalContent()}
            </Modal>

            {/* Force-renew modal — 单独，因为要扣费 */}
            <Modal
                title={<Space><DollarOutlined />强制续费（管理员代扣）</Space>}
                open={renewModal.visible}
                onOk={executeRenew}
                onCancel={() => setRenewModal({ visible: false, domain: null })}
                confirmLoading={actionLoading}
                okText="确认续费"
                cancelText="取消"
                okButtonProps={{ danger: true, 'data-testid': 'renew-confirm-btn' }}
                destroyOnClose
            >
                {renewModal.domain && (
                    <>
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="此操作会向注册商发起真实续费请求并扣除用户余额"
                            description={
                                <div>
                                    <div>域名: <Text strong>{renewModal.domain.domain}</Text></div>
                                    <div>所属用户: <Text strong>{renewModal.domain.username || renewModal.domain.user?.username} (ID: {renewModal.domain.user_id})</Text></div>
                                    <div>注册商: <Text strong>{renewModal.domain.registrar_name || renewModal.domain.registrar?.name || '-'}</Text></div>
                                </div>
                            }
                        />
                        <Form form={renewForm} layout="vertical">
                            <Form.Item name="years" label="续费年数" rules={[{ required: true }]}>
                                <InputNumber min={1} max={10} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="confirm_domain"
                                label={`输入 "${renewModal.domain.domain}" 以确认`}
                                rules={[{ required: true, message: '请确认域名' }]}
                            >
                                <Input placeholder={renewModal.domain.domain} />
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* Bulk modal */}
            <Modal
                title={`批量操作（${selectedRowKeys.length} 个域名）`}
                open={bulkModal.visible}
                onOk={executeBulk}
                onCancel={() => setBulkModal({ visible: false, type: '' })}
                confirmLoading={bulkLoading}
                okText={bulkResult ? '关闭' : '执行'}
                cancelText="取消"
                okButtonProps={{
                    danger: ['suspend', 'hold'].includes(bulkModal.type),
                }}
                width={600}
                destroyOnClose
            >
                {!bulkResult ? (
                    <>
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`你正在对 ${selectedRowKeys.length} 个域名执行 ${bulkModal.type} 操作`}
                            description="此操作会逐个调用对应 API（后端暂无真正的批量端点，详见 ADMIN_DOMAIN_UX_FINDINGS.md gap #2-3）。中途失败的项目会显示在结果列表里。"
                        />
                        {BULK_CONFIRM_KEYWORD[bulkModal.type] && (
                            <Form layout="vertical" style={{ marginTop: 4 }}>
                                <Form.Item
                                    label={`高危操作 — 请输入 "${BULK_CONFIRM_KEYWORD[bulkModal.type]}" 以确认对 ${selectedRowKeys.length} 个域名执行批量${bulkModal.type === 'suspend' ? '暂停' : 'Hold'}`}
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input
                                        value={bulkConfirmText}
                                        onChange={(e) => setBulkConfirmText(e.target.value)}
                                        placeholder={BULK_CONFIRM_KEYWORD[bulkModal.type]}
                                        data-testid="bulk-confirm-input"
                                    />
                                </Form.Item>
                            </Form>
                        )}
                    </>
                ) : (
                    <>
                        <Paragraph>
                            <Text strong>成功:</Text> {bulkResult.ok}{' '}
                            <Text strong>失败:</Text> {bulkResult.fail}
                        </Paragraph>
                        <div style={{ maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                            {bulkResult.results.map((r, i) => (
                                <div key={i} style={{ color: r.ok ? '#52c41a' : '#f5222d' }}>
                                    {r.ok ? '✓' : '✗'} {r.id ? `domain#${r.id}` : 'batch'} {r.msg || r.count ? `— ${r.msg || `${r.count} scheduled`}` : ''}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Modal>

            {/* 从注册商同步既有域名 */}
            <Modal
                title={<Space><CloudSyncOutlined />从注册商同步既有域名</Space>}
                open={syncModal.visible}
                onOk={executeSync}
                onCancel={() => setSyncModal({ visible: false })}
                confirmLoading={syncLoading}
                okText={syncResult ? '再次同步' : '开始同步'}
                cancelText="关闭"
                width={600}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="拉取注册商账户下的域名并更新平台内已存在的匹配记录（状态/到期/锁定/隐私/NS）。"
                    description="仅更新平台内已有的域名记录，不会自动创建未关联用户的新域名。"
                />
                <Form form={syncForm} layout="vertical">
                    <Form.Item name="registrar_id" label="注册商" rules={[{ required: true, message: '请选择注册商' }]}>
                        <Select
                            placeholder="选择注册商"
                            options={registrars.map((r) => ({ value: r.id, label: r.name }))}
                        />
                    </Form.Item>
                </Form>
                {syncResult && (
                    <Alert
                        type="success"
                        showIcon
                        message={`共 ${syncResult.total ?? 0} 个，更新 ${syncResult.synced ?? 0} 个，失败 ${syncResult.errors?.length ?? 0} 个`}
                        description={syncResult.errors?.length ? (
                            <div style={{ maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                                {syncResult.errors.map((e, i) => (
                                    <div key={i} style={{ color: '#f5222d' }}>✗ {e.domain} — {e.error}</div>
                                ))}
                            </div>
                        ) : null}
                    />
                )}
            </Modal>

            {/* 宽限/赎回期域名 */}
            <Modal
                title={<Space><WarningOutlined />宽限 / 赎回期域名</Space>}
                open={graceModal.visible}
                onCancel={() => setGraceModal({ visible: false })}
                footer={null}
                width={900}
                destroyOnClose
            >
                <Space style={{ marginBottom: 12 }} wrap>
                    <Select
                        placeholder="期限"
                        style={{ width: 160 }}
                        allowClear
                        value={gracePeriod}
                        onChange={(v) => { setGracePeriod(v); loadGraceDomains(v); }}
                        options={[
                            { value: 'grace', label: '宽限期 (Grace)' },
                            { value: 'redemption', label: '赎回期 (Redemption)' },
                        ]}
                    />
                    <Button icon={<ReloadOutlined />} onClick={() => loadGraceDomains(gracePeriod)} loading={graceLoading}>
                        刷新
                    </Button>
                </Space>
                <Table
                    size="small"
                    rowKey="id"
                    loading={graceLoading}
                    dataSource={graceRows}
                    scroll={{ x: 800 }}
                    pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
                    columns={[
                        { title: '域名', dataIndex: 'domain', key: 'domain' },
                        { title: '用户', key: 'user', render: (_, r) => r.user?.username || r.user_id },
                        { title: '注册商', key: 'registrar', render: (_, r) => r.registrar?.name || '-' },
                        {
                            title: '状态', key: 'period', render: (_, r) => (
                                <Space size={4}>
                                    {r.in_grace && <Tag color="orange">宽限 {r.grace_days_left}天</Tag>}
                                    {r.in_redemption && <Tag color="red">赎回 {r.redemption_days_left}天</Tag>}
                                </Space>
                            )
                        },
                        {
                            title: '操作', key: 'action', render: (_, r) => (
                                <Button
                                    size="small"
                                    type="primary"
                                    disabled={!r.in_redemption}
                                    loading={restoringId === r.id}
                                    onClick={() => Modal.confirm({
                                        title: '确认恢复域名',
                                        content: `确定向注册商发起恢复请求恢复 ${r.domain}？可能产生赎回费用。`,
                                        okText: '确认恢复',
                                        cancelText: '取消',
                                        onOk: () => handleRestore(r),
                                    })}
                                >
                                    恢复
                                </Button>
                            )
                        },
                    ]}
                />
            </Modal>

            {/* 批量导入既有域名记录 */}
            <Modal
                title={<Space><ImportOutlined />批量导入既有域名</Space>}
                open={importModal.visible}
                onOk={executeImport}
                onCancel={() => setImportModal({ visible: false })}
                confirmLoading={importLoading}
                okText={importResult ? '再次导入' : '开始导入'}
                cancelText="关闭"
                width={640}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="导入已在注册商存在的域名到平台记录，并关联到指定用户。"
                    description="每行一个域名，格式：domain,user_id[,registrar_order_id]。此操作仅创建/更新本地记录，不会真实注册域名。"
                />
                <Form form={importForm} layout="vertical">
                    <Form.Item name="registrar_id" label="注册商" rules={[{ required: true, message: '请选择注册商' }]}>
                        <Select
                            placeholder="选择注册商"
                            options={registrars.map((r) => ({ value: r.id, label: r.name }))}
                        />
                    </Form.Item>
                    <Form.Item name="rows" label="域名列表" rules={[{ required: true, message: '请输入至少一行' }]}>
                        <Input.TextArea
                            rows={8}
                            placeholder={'example.com,1001\nexample.net,1002,ORDER-123'}
                            style={{ fontFamily: 'monospace' }}
                        />
                    </Form.Item>
                </Form>
                {importResult && (
                    <Alert
                        type="success"
                        showIcon
                        message={`共 ${importResult.total ?? 0} 个，成功 ${importResult.imported ?? 0} 个，失败 ${importResult.errors?.length ?? 0} 个`}
                        description={importResult.errors?.length ? (
                            <div style={{ maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                                {importResult.errors.map((e, i) => (
                                    <div key={i} style={{ color: '#f5222d' }}>✗ {e.domain || ''} — {e.error || e.message}</div>
                                ))}
                            </div>
                        ) : null}
                    />
                )}
            </Modal>

            {/* 单域名同步日志 */}
            <Modal
                title={<Space><HistoryOutlined />同步日志{syncLogModal.domain ? ` — ${syncLogModal.domain.domain}` : ''}</Space>}
                open={syncLogModal.visible}
                onCancel={() => setSyncLogModal({ visible: false, domain: null })}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Table
                    size="small"
                    rowKey={(r, i) => r.id ?? i}
                    loading={syncLogLoading}
                    dataSource={syncLogRows}
                    scroll={{ x: 700 }}
                    pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
                    columns={[
                        {
                            title: '时间',
                            dataIndex: 'created_at',
                            key: 'created_at',
                            width: 160,
                            render: (d) => d ? moment(d).format('YYYY-MM-DD HH:mm:ss') : '-',
                        },
                        {
                            title: '类型',
                            dataIndex: 'sync_type',
                            key: 'sync_type',
                            width: 120,
                            render: (t) => t || '-',
                        },
                        {
                            title: '结果',
                            dataIndex: 'status',
                            key: 'status',
                            width: 90,
                            render: (s) => {
                                const ok = s === 'success' || s === 'ok' || s === 1 || s === '1';
                                return <Tag color={ok ? 'success' : 'error'}>{ok ? '成功' : (s || '失败')}</Tag>;
                            },
                        },
                        {
                            title: '说明',
                            dataIndex: 'message',
                            key: 'message',
                            render: (m, r) => m || r.error || r.detail || '-',
                        },
                    ]}
                />
            </Modal>
        </div>
    );
};

export default DomainManagement;
